import { faInfoCircle, faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MessageTemplateAutocomplete from 'components/common/MessageTemplateAutocomplete';
import MessageTemplateSelector from 'components/common/MessageTemplateSelector';
import Editor from 'components/editor/Editor';
import Loading from 'components/loading/Loading';
import useApi from 'helpers/api';
import {
  calculateEndTimeFromStartAndTimeSpent,
  calculateStartTimeFromEndAndTimeSpent,
  calculateTimeSpent
} from 'helpers/timeCalculations';
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import AppointmentsService from 'services/Appointments.service';
import PauseReasonsService from 'services/PauseReasons.service';

const TicketAppointmentEditModal = ({
  ticketId,
  data,
  showModal,
  handleClose,
  refreshList,
  refreshLogs,
  refreshTicket
}) => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const api = useApi();
  const form = useRef(null);
  const editorRef = useRef(null);
  const { authenticatedUser } = useAuthenticatedUser();
  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Armazenar o startTime original para comparação na edição
  const originalStartTimeRef = useRef('');
  const originalDateRef = useRef('');
  const hasStoredOriginalValues = useRef(false);

  const [body, setBody] = useState({
    description: '',
    typeService: '',
    timeSpent: '',
    startTime: '',
    endTime: '',
    date: '',
    pauseSla: false,
    pauseReason: { id: '' },
    user: { id: '' },
    backdateReason: '',
    slaEligible: false
  });
  const [pauseReasons, setPauseReasons] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters] = useState({ includeInactives: false });

  useEffect(() => {
    getPauseReasons();
    getAgents();
  }, []);

  // Resetar a flag quando o modal abrir
  useEffect(() => {
    if (showModal) {
      hasStoredOriginalValues.current = false;
    }
  }, [showModal]);

  useEffect(() => {
    if (data) {
      // Extrair apenas a data se for datetime-local
      let dateValue = '';
      let timeFromDate = '';
      if (data.date) {
        if (data.date.includes('T')) {
          const dateParts = data.date.split('T');
          dateValue = dateParts[0];
          // Extrair hora da data (formato: YYYY-MM-DDTHH:MM ou YYYY-MM-DDTHH:MM:SS)
          if (dateParts[1]) {
            const timePart = dateParts[1].split(':');
            if (timePart.length >= 2) {
              timeFromDate = `${timePart[0].padStart(
                2,
                '0'
              )}:${timePart[1].padStart(2, '0')}`;
            }
          }
        } else {
          dateValue = data.date;
        }
      }

      // Tratar o campo user - pode vir como objeto ou número
      let userId = '';
      if (data.user) {
        if (typeof data.user === 'object' && data.user.id) {
          userId = data.user.id;
        } else if (typeof data.user === 'number') {
          userId = data.user;
        }
      } else if (data.userId) {
        userId = data.userId;
      } else if (authenticatedUser?.id) {
        userId = authenticatedUser.id;
      }

      const formattedTimeSpent = data.timeSpent
        ? formatTimeSpent(data.timeSpent)
        : '';
      let startTime = data.startTime ? data.startTime : '';
      let endTime = data.endTime ? data.endTime : '';

      // Se não houver hora início e hora fim, mas houver hora na data e tempo gasto
      if (!startTime && !endTime && timeFromDate && formattedTimeSpent) {
        // Colocar a hora da data na hora fim
        endTime = timeFromDate;
        // Calcular hora início baseada no tempo gasto e hora fim
        const calculatedStart = calculateStartTimeFromEndAndTimeSpent(
          endTime,
          formattedTimeSpent
        );
        if (calculatedStart) {
          startTime = calculatedStart;
        }
      }

      // Armazenar valores originais APENAS UMA VEZ quando o modal abrir
      if (!hasStoredOriginalValues.current) {
        originalStartTimeRef.current = startTime;
        originalDateRef.current = dateValue;
        hasStoredOriginalValues.current = true;
      }

      setBody({
        description: data.description ? data.description : '',
        typeService: data.typeService
          ? data.typeService.typeService
          : 'INTERNAL',
        timeSpent: formattedTimeSpent,
        startTime: startTime,
        endTime: endTime,
        date: dateValue,
        pauseSla: false,
        pauseReason: { id: '' },
        user: { id: userId },
        backdateReason: data.backdateReason || '',
        slaEligible: data.slaEligible || false
      });
    }
  }, [data, authenticatedUser]);

  const getPauseReasons = async () => {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await PauseReasonsService.list(queryParams);

    response.content && setPauseReasons(response.content);
  };

  const getAgents = async () => {
    try {
      const queryParams = new URLSearchParams({
        page: 0,
        size: 1000,
        inactive: false
      }).toString();
      const response = await api.agents.list(queryParams);
      if (response.content) {
        setAgents(response.content);
      }
    } catch (error) {
      console.error('Erro ao buscar agentes:', error);
    }
  };

  const prepareBody = () => {
    const bodyToSend = { ...body };
    // Se a data não tiver hora, adicionar hora atual
    if (bodyToSend.date && !bodyToSend.date.includes('T')) {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      bodyToSend.date = `${bodyToSend.date}T${hours}:${minutes}`;
    }
    // Garantir que user seja um objeto
    if (bodyToSend.user && typeof bodyToSend.user === 'number') {
      bodyToSend.user = { id: bodyToSend.user };
    } else if (bodyToSend.user && !bodyToSend.user.id && bodyToSend.user) {
      bodyToSend.user = { id: bodyToSend.user };
    }
    return bodyToSend;
  };

  const editAppointment = async () => {
    setIsLoading(true);
    const bodyToSend = prepareBody();
    const response = await AppointmentsService.edit(ticketId, data.id, bodyToSend);
    if (response.id) {
      showSuccessNotification('Apontamento editado com sucesso');
      refreshTicket();
      refreshList();
      refreshLogs();
      handleClose();
      clearFields();
      setValidated(false);
    } else if (response.status === 403) {
      showErrorNotification(
        'Você não tem permissão para realizar esta operação'
      );
    } else {
      showErrorNotification(
        response.userMessage || 'Erro ao realizar operação'
      );
    }
    setIsLoading(false);
  };

  const isBackdated = () => {
    // Na edição, só considera retroativo se o usuário alterou o horário de início
    // para um horário ANTERIOR ao original
    if (!body.date || !body.startTime) return false;
    if (!originalStartTimeRef.current || !originalDateRef.current) return false;

    // Comparar a data atual com a data original
    if (body.date < originalDateRef.current) return true;
    
    // Se a data for igual, comparar os horários
    if (body.date === originalDateRef.current && body.startTime < originalStartTimeRef.current) {
      return true;
    }
    
    return false;
  };

  const isDescriptionEmpty =
    body.description.trim() === '' || body.description === '<p><br></p>';
  const isBackdateReasonEmpty =
    isBackdated() &&
    (body.backdateReason.trim() === '' ||
      body.backdateReason === '<p><br></p>');

  const onSubmit = e => {
    if (
      form.current.checkValidity() === false ||
      body.timeSpent === '' ||
      body.startTime === '' ||
      body.endTime === '' ||
      isDescriptionEmpty ||
      isBackdateReasonEmpty
    ) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
    } else {
      editAppointment();
    }
  };

  const clearFields = () => {
    setBody({
      description: '',
      typeService: '',
      timeSpent: '',
      startTime: '',
      endTime: '',
      date: '',
      pauseSla: false,
      pauseReason: { id: '' },
      user: { id: authenticatedUser?.id || '' }
    });
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    return now.toISOString().slice(0, 16); // Formata como yyyy-MM-ddTHH:mm
  };

  const today = getCurrentDateTime();

  const formatTimeSpent = timeString => {
    if (!timeString) return '';
    // Se já estiver no formato HH:MM, retornar como está
    if (timeString.match(/^\d{2}:\d{2}$/)) {
      return timeString;
    }
    // Se estiver no formato HH:MM:SS, remover os segundos
    if (timeString.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return timeString.substring(0, 5);
    }
    // Se estiver em outro formato, tentar extrair HH:MM
    const parts = timeString.split(':');
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    return timeString;
  };

  const getTimeSpentStyle = () => {
    if (!(body.startTime && body.endTime)) return {};

    const isDark =
      document.documentElement.getAttribute('data-bs-theme') === 'dark';

    if (isDark) {
      return {
        backgroundColor: 'rgba(0, 210, 122, 0.2)',
        borderColor: 'rgba(0, 210, 122, 0.4)',
        color: '#00d27a'
      };
    }

    return {
      backgroundColor: '#d4edda',
      borderColor: '#c3e6cb'
    };
  };

  const setFieldBody = (fieldName, value) => {
    setBody(prevBody => {
      const newBody = { ...prevBody };

      // Tratar campo user separadamente - sempre manter como objeto { id: number }
      if (fieldName === 'user') {
        newBody.user = { id: value };
      } else {
        newBody[fieldName] = value;
      }

      // Se startTime ou endTime foram alterados, calcular timeSpent quando ambos estiverem preenchidos
      if (fieldName === 'startTime' || fieldName === 'endTime') {
        const start = fieldName === 'startTime' ? value : newBody.startTime;
        const end = fieldName === 'endTime' ? value : newBody.endTime;

        if (start && end) {
          const calculatedTime = calculateTimeSpent(start, end);
          if (calculatedTime) {
            newBody.timeSpent = calculatedTime;
          }
        }
      }

      // Se timeSpent foi alterado, calcular baseado nos campos disponíveis
      if (fieldName === 'timeSpent') {
        const timeSpent = value;

        // Validar formato HH:MM
        if (timeSpent && timeSpent.match(/^\d{2}:\d{2}$/)) {
          const start = newBody.startTime;
          const end = newBody.endTime;

          // Se temos início e tempo gasto, calcular fim
          if (start && !end) {
            const calculatedEnd = calculateEndTimeFromStartAndTimeSpent(
              start,
              timeSpent
            );
            if (calculatedEnd) {
              newBody.endTime = calculatedEnd;
            }
          }
          // Se temos fim e tempo gasto, calcular início
          else if (end && !start) {
            const calculatedStart = calculateStartTimeFromEndAndTimeSpent(
              end,
              timeSpent
            );
            if (calculatedStart) {
              newBody.startTime = calculatedStart;
            }
          }
          // Se temos início e fim, recalcular hora fim baseada no início e no novo tempo gasto
          else if (start && end) {
            const calculatedEnd = calculateEndTimeFromStartAndTimeSpent(
              start,
              timeSpent
            );
            if (calculatedEnd) {
              newBody.endTime = calculatedEnd;
            }
          }
        }
      }

      return newBody;
    });
  };

  return (
    <Modal size="lg" show={showModal} onHide={handleClose}>
      {isLoading && <Loading />}
      <Modal.Header closeButton>
        <Modal.Title>Editar apontamento</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form validated={validated} ref={form}>
          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="appointment.type">
              <Form.Label>
                Tipo de Apontamento <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                aria-label="Tipo"
                value={body.typeService}
                onChange={e => setFieldBody('typeService', e.target.value)}
                required
              >
                <option value="INTERNAL" key="INTERNAL">
                  Interno
                </option>
                <option value="EXTERNAL" key="EXTERNAL">
                  Externo
                </option>
              </Form.Select>
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Escolha uma opção!
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="6" controlId="appointment.agent">
              <Form.Label>
                Agente <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                aria-label="Agente"
                value={body.user?.id || body.user || ''}
                onChange={e =>
                  setFieldBody(
                    'user',
                    e.target.value ? Number(e.target.value) : ''
                  )
                }
                required
              >
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.firstName} {agent.lastName}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Selecione um agente!
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="3" controlId="appointment.date">
              <Form.Label>
                Data <span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={body.date}
                onChange={e => setFieldBody('date', e.target.value)}
                required
                max={today.split('T')[0]}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Selecione uma data!
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="3" controlId="appointment.startTime">
              <Form.Label>
                Hora Início <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="time"
                required
                value={body.startTime}
                onChange={e => setFieldBody('startTime', e.target.value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="3" controlId="appointment.endTime">
              <Form.Label>
                Hora Fim <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="time"
                required
                value={body.endTime}
                onChange={e => setFieldBody('endTime', e.target.value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="3" controlId="appointment.timeSpent">
              <Form.Label>
                Tempo <span className="text-danger"> *</span>
              </Form.Label>
              <MaskedInput
                mask={[/[0-9]/, /\d/, ':', /\d/, /\d/]}
                placeholder="HH:MM"
                required
                guide={false}
                value={body.timeSpent}
                onChange={e => setFieldBody('timeSpent', e.target.value)}
                style={getTimeSpentStyle ? getTimeSpentStyle() : {}}
                render={(ref, props) => <Form.Control ref={ref} {...props} />}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Insira o tempo trabalhado!
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md={2}>
              <Form.Label>Pausar SLA</Form.Label>
              <Form.Switch
                value={body.pauseSla}
                onChange={() => setFieldBody('pauseSla', !body.pauseSla)}
              />
            </Form.Group>

            <Form.Group as={Col} md={3}>
              <Form.Label>
                Regularizar SLA{' '}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="sla-tooltip">
                      Ao habilitar esta opção, se o tempo informado estiver dentro
                      do prazo de resposta, o SLA será marcado como cumprido.
                    </Tooltip>
                  }
                >
                  <span>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="text-secondary cursor-pointer ms-1"
                    />
                  </span>
                </OverlayTrigger>
              </Form.Label>
              <Form.Switch
                value={body.slaEligible}
                checked={body.slaEligible}
                onChange={() => setFieldBody('slaEligible', !body.slaEligible)}
              />
            </Form.Group>

            {body.pauseSla && (
              <Form.Group as={Col} md={4}>
                <Form.Label>
                  Motivo de pausa <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  required={body.pauseReason}
                  value={body.pauseReason.id}
                  onChange={e =>
                    setBody({ ...body, pauseReason: { id: e.target.value } })
                  }
                >
                  <option value="">Selecione</option>
                  {pauseReasons.map((item, i) => (
                    <option value={item.id} key={i}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Motivo de pausa obrigatório.
                </Form.Control.Feedback>
              </Form.Group>
            )}
          </Row>

          <Form.Group controlId="appointment.profile">
            {isBackdated() && (
              <Form.Group
                className="mb-3"
                controlId="appointment.backdateReason"
              >
                <div
                  className="alert alert-warning py-2 mb-2 fs-10"
                  role="alert"
                >
                  <strong>Atenção:</strong> O horário do apontamento é
                  retroativo. Por favor, forneça uma justificativa.
                </div>
                <Form.Label>
                  Justificativa <span className="text-danger">*</span>
                </Form.Label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={body.backdateReason}
                  onChange={e => setFieldBody('backdateReason', e.target.value)}
                  required={isBackdated()}
                />
                {validated && isBackdateReasonEmpty && (
                  <span className="d-block text-danger fs-10 mt-1">
                    Justificativa é obrigatória para apontamentos retroativos.
                  </span>
                )}
              </Form.Group>
            )}
            <Form.Label className="d-flex align-items-center justify-content-between">
              <span>
                Descrição <span className="text-danger"> *</span>
              </span>
              <MessageTemplateSelector
                type="APPOINTMENT"
                ticketId={ticketId}
                editorRef={editorRef}
                onTemplateApplied={content => {
                  setFieldBody('description', content);
                }}
              />
            </Form.Label>
            <div className="position-relative">
              <Editor
                ref={editorRef}
                data={body.description}
                value={value => setFieldBody('description', value)}
              />
              <MessageTemplateAutocomplete
                editorRef={editorRef}
                type="APPOINTMENT"
                ticketId={ticketId}
                onTemplateApplied={content => {
                  setFieldBody('description', content);
                }}
              />
            </div>
            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            {validated && isDescriptionEmpty && (
              <span className="d-block text-danger fs-10 mt-1">
                Campo obrigatório.
              </span>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={handleClose}>
          Fechar
        </Button>
        <Button variant="primary" type="submit" onClick={onSubmit}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TicketAppointmentEditModal;
